import { User } from 'lucide-react';
import { getFrameConfig } from '../../lib/credits';

interface UserAvatarProps {
  avatarUrl?: string | null;
  name?: string;
  size?: 'sm' | 'md' | 'lg';
  activeFrame?: string | null;
}

const PLAIN_SIZES = {
  sm: { container: 'w-8 h-8', icon: 14 },
  md: { container: 'w-12 h-12', icon: 20 },
  lg: { container: 'w-24 h-24', icon: 40 },
};

const FRAME_SIZES = {
  sm: {
    outer: 'w-10 h-10',
    frameEmoji: 'text-[2.8rem]',
    avatarContainer: 'w-5 h-5',
    icon: 10,
    avatarBorder: 'border border-gray-800/80',
  },
  md: {
    outer: 'w-14 h-14',
    frameEmoji: 'text-[4rem]',
    avatarContainer: 'w-7 h-7',
    icon: 14,
    avatarBorder: 'border border-gray-800/80',
  },
  lg: {
    outer: 'w-32 h-32',
    frameEmoji: 'text-[9rem]',
    avatarContainer: 'w-16 h-16',
    icon: 32,
    avatarBorder: 'border-2 border-gray-800/80',
  },
};

export function UserAvatar({ avatarUrl, name, size = 'sm', activeFrame }: UserAvatarProps) {
  const frame = getFrameConfig(activeFrame ?? null);

  if (!frame) {
    const p = PLAIN_SIZES[size];
    return (
      <div className={`${p.container} rounded-full bg-secondary-500/20 flex items-center justify-center overflow-hidden flex-shrink-0`}>
        {avatarUrl ? (
          <img src={avatarUrl} alt={name || 'Avatar'} className="w-full h-full rounded-full object-cover" />
        ) : (
          <User size={p.icon} className="text-secondary-300" />
        )}
      </div>
    );
  }

  const f = FRAME_SIZES[size];

  return (
    <div className={`relative ${f.outer} flex items-center justify-center flex-shrink-0`}>
      {/* Frame artwork — the emoji fills the entire container as the background */}
      <span
        className={`absolute inset-0 flex items-center justify-center ${f.frameEmoji} leading-none select-none pointer-events-none`}
        style={{ filter: 'drop-shadow(0 0 4px rgba(0,0,0,0.6))' }}
      >
        {frame.emoji}
      </span>

      {/* Avatar sits centered on top of the frame art */}
      <div className={`relative z-10 ${f.avatarContainer} rounded-full overflow-hidden ${f.avatarBorder} shadow-lg bg-gray-900 flex items-center justify-center`}>
        {avatarUrl ? (
          <img src={avatarUrl} alt={name || 'Avatar'} className="w-full h-full rounded-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-secondary-500/20">
            <User size={f.icon} className="text-secondary-300" />
          </div>
        )}
      </div>
    </div>
  );
}
